/**
 * Joined buttons that pick one of a few views: `All time`, `Week`, `Month`.
 *
 * One outline around the whole group, hairline dividers between the items and
 * no gaps, so it reads as one control with several positions rather than a row
 * of separate buttons. It is a radiogroup, because that is what it is: one
 * choice, one tab stop, and the arrow keys move the choice.
 *
 * `ButtonGroup` is not this. That one lays out separate actions, such as a
 * modal footer, and stays a plain row.
 *
 * A trailing item can be a real button instead of a position, for the date
 * button at the end of a range group. `tinted` paints it in the brand tint,
 * which is how the measured design says a custom range is applied.
 */
import type { KeyboardEvent, ReactNode } from 'react'
import { forwardRef, useRef } from 'react'
import { cn } from '../lib/cn'
import { FOCUS_RING } from './control-styles'

const GROUP =
  'inline-flex max-w-full items-stretch overflow-hidden rounded-lg border border-line-strong shadow-xs ' +
  'divide-x divide-line-strong'

const ITEM =
  'inline-flex h-10 shrink-0 items-center justify-center gap-2 whitespace-nowrap px-4 text-base font-semibold transition ' +
  '[&>svg]:h-5 [&>svg]:w-5 disabled:cursor-not-allowed disabled:text-ink-faint ' +
  FOCUS_RING

/** An item's paint: selected, tinted, or at rest. */
export function segmentClasses({ selected = false, tinted = false }: { selected?: boolean; tinted?: boolean }): string {
  return cn(
    ITEM,
    tinted
      ? 'bg-primary-50 text-primary-700 hover:bg-primary-100'
      : selected
        ? 'bg-surface-hover text-ink'
        : 'bg-surface text-ink-secondary hover:bg-surface-hover',
  )
}

export interface SegmentOption {
  value: string
  label: ReactNode
  icon?: ReactNode
  disabled?: boolean
}

/** Where the arrow keys take the choice, skipping positions that are off. */
export function nextSegment(options: readonly SegmentOption[], current: number, key: string): number {
  const usable = options.map((option, index) => (option.disabled ? -1 : index)).filter((index) => index >= 0)
  if (usable.length === 0) return -1
  if (key === 'Home') return usable[0] as number
  if (key === 'End') return usable[usable.length - 1] as number
  const step = key === 'ArrowRight' || key === 'ArrowDown' ? 1 : key === 'ArrowLeft' || key === 'ArrowUp' ? -1 : 0
  if (step === 0) return current
  const at = usable.indexOf(current)
  if (at === -1) return usable[0] as number
  return usable[(at + step + usable.length) % usable.length] as number
}

export interface SegmentedGroupProps {
  /** The group's accessible name. */
  label: string
  options: readonly SegmentOption[]
  value: string
  onChange: (value: string) => void
  /** A real button after the positions, such as the date range button. */
  trailing?: ReactNode
  className?: string
}

export function SegmentedGroup({ label, options, value, onChange, trailing, className }: SegmentedGroupProps) {
  const refs = useRef<(HTMLButtonElement | null)[]>([])
  const current = options.findIndex((option) => option.value === value)
  // With nothing chosen the first usable position takes the tab stop, or the
  // group would have none at all.
  const tabStop = current >= 0 ? current : nextSegment(options, -1, 'Home')

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const next = nextSegment(options, current, event.key)
    if (next === current || next < 0) return
    event.preventDefault()
    onChange((options[next] as SegmentOption).value)
    refs.current[next]?.focus()
  }

  return (
    <div className={cn(GROUP, className)}>
      <div role="radiogroup" aria-label={label} onKeyDown={onKeyDown} className="flex items-stretch divide-x divide-line-strong">
        {options.map((option, index) => {
          const selected = index === current
          return (
            <button
              key={option.value}
              ref={(node) => {
                refs.current[index] = node
              }}
              type="button"
              role="radio"
              aria-checked={selected}
              tabIndex={index === tabStop ? 0 : -1}
              disabled={option.disabled}
              onClick={() => onChange(option.value)}
              className={segmentClasses({ selected })}
            >
              {option.icon}
              {option.label}
            </button>
          )
        })}
      </div>
      {trailing}
    </div>
  )
}

export interface SegmentButtonProps {
  children: ReactNode
  onClick?: () => void
  /** The brand tint, for a trailing button whose setting is in force. */
  tinted?: boolean
  icon?: ReactNode
  className?: string
  'aria-label'?: string
  'aria-expanded'?: boolean
  'aria-haspopup'?: 'dialog' | 'menu' | 'listbox' | true
}

/**
 * A trailing item that is an action, drawn to join the group it ends.
 *
 * It forwards its ref because the usual action is opening a popover, and the
 * popover places itself against the button that opened it.
 */
export const SegmentButton = forwardRef<HTMLButtonElement, SegmentButtonProps>(function SegmentButton(
  { children, onClick, tinted = false, icon, className, ...aria },
  ref,
) {
  return (
    <button ref={ref} type="button" onClick={onClick} className={cn(segmentClasses({ tinted }), className)} {...aria}>
      {icon}
      {children}
    </button>
  )
})
